if (!customElements.get('site-header')) {
  customElements.define(
    'site-header',
    class SiteHeader extends HTMLElement {
      connectedCallback() {
        this.selectedBlock = null;

        this.setNodeSelectors();
        this.setStickyHeaderState();
        this.setEventListeners();
        this.setShopifyEditorEvents();
      }

      /**
       * Sets the node selectors.
       */
      setNodeSelectors() {
        this.nodeSelectors = {
          container: this.querySelector('[js-site-header="container"]'),
          header: this.querySelector('[js-site-header="header"]'),
          parentItems: this.querySelectorAll('[js-site-header="parentItem"]'),
          overlay: this.querySelector('[js-site-header="overlay"]'),
          announcementBar: this.querySelector('[js-site-header="announcementBar"]'),
        };
      }

      /**
       * Sets the scroll, resize and mouse event listeners.
       */
      setEventListeners() {
        window.addEventListener('scroll', () => this.setStickyHeaderState());
        window.addEventListener('resize', () => this.setStickyHeaderState());

        this.nodeSelectors.parentItems.forEach((item) => {
          item.addEventListener('mouseenter', ({ currentTarget }) => this.handleMouseEnter(currentTarget));
          item.addEventListener('mouseleave', ({ currentTarget }) => this.handleMouseLeave(currentTarget));
        });
      }

      /**
       * Handles the menu item `mouseenter` event.
       * @param {HTMLElement} item - The target element.
       * @param {Boolean} isUpdating - Prevents re-calculating of layouts.
       */
      handleMouseEnter(item, isUpdating = false) {
        this.setItemState(item, true);

        if (!isUpdating) {
          this.setOverlayOffset();
        }

        this.setOverlayState(true);
        this.setBodyOverflow('hidden');

        /**
         * Close other dropdowns.
         */
        [...this.nodeSelectors.parentItems]
          .filter((element) => element !== item)
          .forEach((element) => {
            this.setItemState(element, false);
          });
      }

      /**
       * Handles the menu item `mouseleave` event.
       * @param {HTMLElement} item - The target element.
       */
      handleMouseLeave(item) {
        /**
         * Prevent closing the dropdown if it contains the selected block.
         */
        if (this.selectedBlock && item.contains(this.selectedBlock)) {
          return;
        }

        this.setItemState(item);
        this.setOverlayState();
        this.setBodyOverflow();
      }

      /**
       * Sets the overlay's dynamic offset.
       * - If the header is sticky, use the height of that.
       * - Else, use the entire component height.
       */
      setOverlayOffset() {
        if (this.nodeSelectors.header.classList.contains('is-sticky')) {
          this.nodeSelectors.overlay.style.setProperty('--offset', `${this.nodeSelectors.header.offsetHeight}px`);
          return;
        }

        this.nodeSelectors.overlay.style.setProperty('--offset', `${this.offsetHeight}px`);
      }

      /**
       * Sets the body's overflow state.
       * @param {'hidden'} value - The overflow value.
       */
      setBodyOverflow(value) {
        if (value === 'hidden') {
          document.body.classList.add('overflow-hidden');
          return;
        }

        document.body.classList.remove('overflow-hidden');
      }

      /**
       * Sets the overlay visible state.
       * @param {Boolean} show - Shows the overlay.
       */
      setOverlayState(show = false) {
        if (show) {
          this.nodeSelectors.overlay.classList.add('is-active');
          return;
        }

        this.nodeSelectors.overlay.classList.remove('is-active');
      }

      /**
       * Sets the item's active state.
       * @param {HTMLElement} item - The item.
       * @param {Boolean} active - The active state.
       */
      setItemState(item, active = false) {
        if (active) {
          item.classList.add('is-mouseenter');
          return;
        }

        item.classList.remove('is-mouseenter');
      }

      /**
       * Sets the sticky header state, and applies an offset.
       */
      setStickyHeaderState() {
        const bottom =
          this.nodeSelectors.container.getBoundingClientRect().top + this.nodeSelectors.container.offsetHeight;

        if (bottom <= 0) {
          this.nodeSelectors.container.style.height = `${this.nodeSelectors.header.offsetHeight}px`;
          this.nodeSelectors.header.classList.add('is-sticky');

          requestAnimationFrame(() => {
            this.setStickyHeaderOffset();
          });

          if (this.nodeSelectors.announcementBar) {
            document.body.style.setProperty('--announcement-bar-offset', '0px');
          }

          return;
        }

        this.nodeSelectors.container.style.height = 'auto';
        this.nodeSelectors.header.classList.remove('is-sticky');

        requestAnimationFrame(() => {
          this.setStickyHeaderOffset();
        });

        if (this.nodeSelectors.announcementBar) {
          document.body.style.setProperty('--announcement-bar-offset', 'var(--announcement-bar-height)');
        }
      }

      /**
       * Sets the offset value for the header.
       * - To be used by sticky elements.
       */
      setStickyHeaderOffset() {
        document.body.style.setProperty('--sticky-header-offset', `${this.nodeSelectors.header.offsetHeight}px`);
      }

      /**
       * Sets the Shopify theme editor event listeners.
       */
      setShopifyEditorEvents() {
        /**
         * When a block is selected, open its parent dropdown.
         */
        document.addEventListener('shopify:block:select', ({ detail }) => {
          this.selectedBlock = document.getElementById(detail.blockId);

          if (this.selectedBlock && this.contains(this.selectedBlock)) {
            const menuItem = this.selectedBlock.closest('[js-site-header="parentItem"]');

            if (menuItem) {
              this.handleMouseEnter(menuItem, detail.load);
            }
          }
        });

        /**
         * When a block is deselected, close its parent dropdown.
         */
        document.addEventListener('shopify:block:deselect', ({ detail }) => {
          const deselectedBlock = document.getElementById(detail.blockId);

          this.selectedBlock = null;

          if (deselectedBlock && this.contains(deselectedBlock)) {
            const menuItem = deselectedBlock.closest('[js-site-header="parentItem"]');

            if (menuItem) {
              this.handleMouseLeave(menuItem);
            }
          }
        });
      }
    }
  );
}
